import {getBaseStickyHeaderHeight, observeStickyHeaderHeight, unobserveStickyHeaderHeight} from '@github-ui/sticky-css'
import {type RefObject, useCallback, useEffect, useRef, useState, useSyncExternalStore} from 'react'

import {VALUES} from '../constants/values'
import {useHasScrolledForHighlight} from './use-has-scrolled-for-highlight'

function subscribeToStickyHeaderHeight(notify: () => void) {
  observeStickyHeaderHeight(notify)
  return () => unobserveStickyHeaderHeight(notify)
}

export const useNewScrollToHighlighted = (
  isHighlightLoaded: boolean,
  highlightedItemRef: RefObject<HTMLLIElement | null>,
  highlightedEventId: string | undefined,
  highlightedReadyToScroll: boolean,
) => {
  const [lastScrolledItemId, setLastScrolledItemId] = useState<string | undefined>(undefined)
  // `hasScrolled` resets when the highlight target changes (and the new target is
  // loaded), so a user navigating between highlights triggers a fresh scroll.
  const [hasScrolled, setHasScrolled] = useHasScrolledForHighlight(
    isHighlightLoaded,
    highlightedEventId,
    lastScrolledItemId,
  )

  const notificationShelfHeight = useSyncExternalStore(
    subscribeToStickyHeaderHeight,
    getBaseStickyHeaderHeight,
    getBaseStickyHeaderHeight,
  )
  const previousNotificationShelfHeightRef = useRef(notificationShelfHeight)

  const scrollToHighlightedEvent = useCallback(
    async (ref: RefObject<HTMLLIElement | null>) => {
      if (!ref.current || !highlightedReadyToScroll) return

      await mediaLoaded()

      ref.current?.scrollIntoView({behavior: 'instant', block: 'start', inline: 'start'})
      window.scrollBy({top: -(VALUES.stickyHeaderHeight + notificationShelfHeight), behavior: 'instant'})
      setLastScrolledItemId(highlightedEventId)
      setHasScrolled(true)
    },
    [highlightedEventId, highlightedReadyToScroll, notificationShelfHeight, setHasScrolled],
  )

  // The shelf can arrive after the highlighted item has already scrolled. Its shared height store
  // notifies us on insertion/removal, so rerun the scroll with the current offset.
  useEffect(() => {
    if (previousNotificationShelfHeightRef.current !== notificationShelfHeight) {
      previousNotificationShelfHeightRef.current = notificationShelfHeight
      setHasScrolled(false)
    }
  }, [notificationShelfHeight, setHasScrolled])

  // Scroll to the highlighted item if it's loaded.
  useEffect(() => {
    if (!isHighlightLoaded || !highlightedItemRef?.current || hasScrolled) return

    scrollToHighlightedEvent(highlightedItemRef)
  }, [hasScrolled, highlightedItemRef, isHighlightLoaded, scrollToHighlightedEvent])
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
// resolves when comment body videos have loaded enough data to render the preview image
async function videosReady(): Promise<unknown> {
  const videos: NodeListOf<HTMLVideoElement> = document.querySelectorAll(VALUES.commentVideo)
  const videoLoads = Array.from(videos).map(v => {
    return new Promise<HTMLVideoElement>(resolve => {
      if (v.readyState >= v.HAVE_METADATA) {
        resolve(v)
      } else {
        // don't wait forever :)
        const timeout = setTimeout(() => resolve(v), VALUES.scrollWaitMediaTimeout)
        const done = () => {
          clearTimeout(timeout)
          resolve(v)
        }
        v.addEventListener('loadeddata', () => {
          if (v.readyState >= v.HAVE_METADATA) done()
        })
        v.addEventListener('error', () => done())
      }
    })
  })
  return Promise.all(videoLoads)
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
// resolves when comment body images are loaded
async function imagesReady(): Promise<unknown> {
  const images: NodeListOf<HTMLImageElement> = document.querySelectorAll(VALUES.commentImage)
  const imageLoads = Array.from(images).map(i => {
    new Promise<HTMLImageElement>(resolve => {
      if (i.complete) {
        resolve(i)
      } else {
        const timeout = setTimeout(() => resolve(i), VALUES.scrollWaitMediaTimeout)
        const done = () => {
          clearTimeout(timeout)
          resolve(i)
        }
        i.addEventListener('load', () => done())
        i.addEventListener('error', () => done())
      }
    })
  })
  return Promise.all(imageLoads)
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
async function mediaLoaded(): Promise<unknown> {
  return Promise.all([videosReady(), imagesReady()])
}
